/**
 * TipTapEditor.tsx
 * TipTap 기반 리치 텍스트 에디터 컴포넌트
 * - 텍스트 서식 (Bold, Italic, Underline, Strike)
 * - 헤딩 (H1, H2, H3)
 * - 리스트 (순서, 비순서)
 * - 텍스트 색상
 * - 링크, 이미지 삽입
 * - 텍스트 정렬
 */

import { useCallback, useRef, useState } from 'react';
import { auth, storage } from '../../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import {
  Box,
  IconButton,
  Tooltip,
  Divider,
  Popover,
  TextField,
  Button,
  Typography,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  FormatListBulleted,
  FormatListNumbered,
  InsertLink,
  LinkOff,
  Image as ImageIcon,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatColorText,
  FormatClear,
  Undo,
  Redo,
} from '@mui/icons-material';

interface TipTapEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

// 프리셋 색상
const COLORS = [
  '#000000', '#434343', '#666666', '#999999',
  '#dc2626', '#ea580c', '#d97706', '#65a30d',
  '#059669', '#0891b2', '#2563eb', '#7c3aed',
  '#c026d3', '#e11d48',
];

export default function TipTapEditor({ value, onChange, placeholder = '내용을 입력하세요...', minHeight = 200 }: TipTapEditorProps) {
  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageAnchor, setImageAnchor] = useState<HTMLElement | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextStyle,
      Color,
      Image.configure({
        HTMLAttributes: { style: 'max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0;' },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { style: 'color: #2563eb; text-decoration: underline;' },
      }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // 링크 삽입
  const handleLinkOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (!editor) return;
    const existingLink = editor.getAttributes('link').href || '';
    setLinkUrl(existingLink);
    setLinkAnchor(event.currentTarget);
  }, [editor]);

  const handleLinkSubmit = useCallback(() => {
    if (!editor) return;
    if (linkUrl) {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setLinkAnchor(null);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const handleUnlink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
  }, [editor]);

  // 이미지 삽입 (URL)
  const handleImageOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setImageUrl('');
    setImageAnchor(event.currentTarget);
  }, []);

  const handleImageSubmit = useCallback(() => {
    if (!editor || !imageUrl) return;
    editor.chain().focus().setImage({ src: imageUrl }).run();
    setImageAnchor(null);
    setImageUrl('');
  }, [editor, imageUrl]);

  // 이미지 삽입 (파일 → Firebase Storage 직접 업로드 → URL)
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;

    try {
      setUploading(true);
      // Firebase Storage에 직접 업로드
      const ext = file.name.split('.').pop() || 'png';
      const storageRef = ref(storage, `notice-images/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const downloadUrl = await getDownloadURL(storageRef);
      editor.chain().focus().setImage({ src: downloadUrl }).run();
    } catch (err) {
      console.error('[TipTapEditor] 이미지 업로드 실패:', err);
      alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [editor]);

  if (!editor) return null;

  const ToolBtn = ({ icon, tooltip, active, onClick, disabled }: {
    icon: React.ReactNode; tooltip: string; active?: boolean; onClick: () => void; disabled?: boolean;
  }) => (
    <Tooltip title={tooltip} arrow>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            borderRadius: 1,
            width: 32,
            height: 32,
            color: active ? '#2563eb' : '#555',
            bgcolor: active ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
            '&:hover': { bgcolor: active ? 'rgba(37, 99, 235, 0.12)' : 'rgba(0,0,0,0.04)' },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  const HeadingBtn = ({ level, label }: { level: 1 | 2 | 3; label: string }) => (
    <Tooltip title={`제목 ${level}`} arrow>
      <span>
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
          sx={{
            borderRadius: 1,
            width: 32,
            height: 32,
            fontSize: '0.75rem',
            fontWeight: 800,
            color: editor.isActive('heading', { level }) ? '#2563eb' : '#555',
            bgcolor: editor.isActive('heading', { level }) ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          {label}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{
      border: '1px solid #d1d5db',
      borderRadius: 2,
      overflow: 'hidden',
      '&:focus-within': {
        borderColor: '#2563eb',
        boxShadow: '0 0 0 2px rgba(37, 99, 235, 0.1)',
      },
      transition: 'all 0.2s',
    }}>
      {/* 툴바 */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.3,
        px: 1,
        py: 0.5,
        bgcolor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
      }}>
        {/* Undo / Redo */}
        <ToolBtn icon={<Undo sx={{ fontSize: 18 }} />} tooltip="되돌리기" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} />
        <ToolBtn icon={<Redo sx={{ fontSize: 18 }} />} tooltip="다시 실행" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.3, my: 0.5 }} />

        {/* 헤딩 */}
        <HeadingBtn level={1} label="H1" />
        <HeadingBtn level={2} label="H2" />
        <HeadingBtn level={3} label="H3" />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.3, my: 0.5 }} />

        {/* 텍스트 서식 */}
        <ToolBtn icon={<FormatBold sx={{ fontSize: 18 }} />} tooltip="굵게" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
        <ToolBtn icon={<FormatItalic sx={{ fontSize: 18 }} />} tooltip="기울임" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
        <ToolBtn icon={<FormatUnderlined sx={{ fontSize: 18 }} />} tooltip="밑줄" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
        <ToolBtn icon={<StrikethroughS sx={{ fontSize: 18 }} />} tooltip="취소선" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.3, my: 0.5 }} />

        {/* 텍스트 색상 */}
        <ToolBtn
          icon={<FormatColorText sx={{ fontSize: 18 }} />}
          tooltip="텍스트 색상"
          onClick={(e: any) => setColorAnchor(e.currentTarget)}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.3, my: 0.5 }} />

        {/* 정렬 */}
        <ToolBtn icon={<FormatAlignLeft sx={{ fontSize: 18 }} />} tooltip="왼쪽 정렬" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} />
        <ToolBtn icon={<FormatAlignCenter sx={{ fontSize: 18 }} />} tooltip="가운데 정렬" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} />
        <ToolBtn icon={<FormatAlignRight sx={{ fontSize: 18 }} />} tooltip="오른쪽 정렬" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.3, my: 0.5 }} />

        {/* 리스트 */}
        <ToolBtn icon={<FormatListBulleted sx={{ fontSize: 18 }} />} tooltip="글머리 기호" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
        <ToolBtn icon={<FormatListNumbered sx={{ fontSize: 18 }} />} tooltip="번호 매기기" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.3, my: 0.5 }} />

        {/* 링크 */}
        <ToolBtn icon={<InsertLink sx={{ fontSize: 18 }} />} tooltip="링크 삽입" active={editor.isActive('link')} onClick={handleLinkOpen as any} />
        {editor.isActive('link') && (
          <ToolBtn icon={<LinkOff sx={{ fontSize: 18 }} />} tooltip="링크 제거" onClick={handleUnlink} />
        )}

        {/* 이미지 */}
        <ToolBtn icon={<ImageIcon sx={{ fontSize: 18 }} />} tooltip="이미지 삽입" onClick={handleImageOpen as any} />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.3, my: 0.5 }} />

        {/* 서식 제거 */}
        <ToolBtn icon={<FormatClear sx={{ fontSize: 18 }} />} tooltip="서식 제거" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} />
      </Box>

      {/* 에디터 본문 */}
      <Box sx={{
        '& .tiptap': {
          minHeight,
          px: 2,
          py: 1.5,
          outline: 'none',
          fontSize: 14,
          lineHeight: 1.7,
          color: '#333',
          '& p': { m: 0, mb: 0.5 },
          '& h1': { fontSize: '1.5rem', fontWeight: 700, mt: 1, mb: 0.5 },
          '& h2': { fontSize: '1.25rem', fontWeight: 700, mt: 1, mb: 0.5 },
          '& h3': { fontSize: '1.1rem', fontWeight: 700, mt: 1, mb: 0.5 },
          '& ul, & ol': { pl: 3, my: 0.5 },
          '& li': { mb: 0.3 },
          '& a': { color: '#2563eb', textDecoration: 'underline' },
          '& img': { maxWidth: '100%', height: 'auto', borderRadius: '8px', my: 1 },
          '& p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            color: '#adb5bd',
            pointerEvents: 'none',
            float: 'left',
            height: 0,
          },
        },
      }}>
        <EditorContent editor={editor} />
      </Box>

      {/* 숨겨진 파일 input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      {/* 링크 팝오버 */}
      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={() => setLinkAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, borderRadius: 2, width: 320 } } }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>링크 삽입</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="https://..."
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleLinkSubmit()}
          autoFocus
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setLinkAnchor(null)}>취소</Button>
          <Button size="small" variant="contained" onClick={handleLinkSubmit}>적용</Button>
        </Box>
      </Popover>

      {/* 이미지 팝오버 */}
      <Popover
        open={Boolean(imageAnchor)}
        anchorEl={imageAnchor}
        onClose={() => setImageAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, borderRadius: 2, width: 340 } } }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>이미지 삽입</Typography>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<ImageIcon />}
          onClick={() => { fileInputRef.current?.click(); setImageAnchor(null); }}
          sx={{ mb: 2, py: 1.2, borderStyle: 'dashed', textTransform: 'none' }}
        >
          PC에서 이미지 선택
        </Button>

        <Divider sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">또는 URL 입력</Typography>
        </Divider>

        <TextField
          fullWidth
          size="small"
          placeholder="https://... 이미지 주소"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleImageSubmit()}
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => setImageAnchor(null)}>취소</Button>
          <Button size="small" variant="contained" onClick={handleImageSubmit} disabled={!imageUrl}>적용</Button>
        </Box>
      </Popover>

      {/* 색상 팝오버 */}
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, borderRadius: 2 } } }}
      >
        <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block', color: '#555' }}>텍스트 색상</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
          {COLORS.map((color) => (
            <IconButton
              key={color}
              size="small"
              onClick={() => { editor.chain().focus().setColor(color).run(); setColorAnchor(null); }}
              sx={{
                width: 28,
                height: 28,
                bgcolor: color,
                border: '2px solid',
                borderColor: editor.getAttributes('textStyle').color === color ? '#2563eb' : 'transparent',
                borderRadius: '50%',
                '&:hover': { opacity: 0.8, transform: 'scale(1.15)' },
                transition: 'all 0.15s',
              }}
            />
          ))}
        </Box>
        <Button
          fullWidth
          size="small"
          onClick={() => { editor.chain().focus().unsetColor().run(); setColorAnchor(null); }}
          sx={{ mt: 1, textTransform: 'none', fontSize: '0.7rem' }}
        >
          색상 초기화
        </Button>
      </Popover>
    </Box>
  );
}
